import { Link } from "react-router";
import type { CharacterState, HomeSnapshot } from "@/shared/contracts";
import { sceneAsset } from "@/features/home/PetFigure";
import { mapFocusHref } from "@/features/world_map/mapFocus";
import { HOME_TAB } from "@/app/primaryTab";
import { Icon, PetAvatar } from "@/shared/ui";
import { serverNow } from "@/shared/time/clock";
import { currentLife, playWhen, type PlayStatus } from "./playStatus";

/**
 * 不能开局时「现在能做的事」（R27 第 4 条：夜里、TA 出门时，玩的页面不能是一张白纸）：一张 TA 的小图、什么时候能玩、去哪儿。
 * 和上面那条原因（role=status，选游戏页与游戏页共用 playNotice）分开放：原因那句一字不变，这里只是补上画面和出口。
 * - 小图只用现成的：睡着而这一套形象有睡姿，就画睡姿；不然画 TA 的头像（照片、物种插画或爪印，同全站头像的退回链）。
 *   **没有睡姿时不拿站姿冒充**（同 PetFigure 的规矩）；出门时头像旁边挂一个出行的小标。
 * - 出口：睡着、刚到家去通讯器说句话（睡着也能说，醒了再回）；出门去地图看看 TA 走到哪（mapFocusHref，聚焦这只宠物）。
 *   `withHome`：游戏页这里另给一个回小屋（选游戏页的回小屋在原因条里，不重复）。
 * - 读不到 TA 在做什么（unknown）：不编时间、没有去处，这张卡只剩一个头像，是一张空的大卡（cand-110 夜间实机评审，UI-PLAY-UNKNOWN-01）。
 *   选游戏页不放（原因条里已有回小屋）；游戏页照放，里面有回小屋。
 * 字都不小于 13px（play.css）；看得见的字里不写 AI、模型、生成。
 */
export function PlayUnavailable({ status, snapshot, character, withHome = false }: {
  status: Exclude<PlayStatus, "ok">;
  snapshot: HomeSnapshot;
  character: CharacterState | null;
  withHome?: boolean;
}) {
  if (status === "unknown" && !withHome) return null; // 读不到时选游戏页不放这张卡：只剩头像、没有可做的事（UI-PLAY-UNKNOWN-01）
  const pet = snapshot.pet;
  const name = pet.name || "TA";
  const when = playWhen(status, currentLife(snapshot), serverNow());
  const sleeping = status === "asleep" ? sceneAsset(character, "sleeping") : null;
  return (
    <section className={`ps-play__away is-${status}`} aria-label="现在能做的事">
      <div className="ps-play__away-art">
        {sleeping ? (
          <img className="ps-play__away-pose" src={sleeping.url} alt={`${name}在睡觉`} />
        ) : (
          <span className="ps-play__away-portrait">
            <PetAvatar petId={pet.pet_id} name={name} species={pet.species} photoUrl={pet.photo_url} origin={pet.origin} size={88} />
            {status === "away" ? (
              <span className="ps-play__away-badge" aria-hidden="true">
                <Icon name="journey" size={18} />
              </span>
            ) : null}
          </span>
        )}
      </div>
      {when ? <p className="ps-play__away-when">{when}</p> : null}
      <div className="ps-play__away-actions">
        {status === "away" ? (
          <Link className="ps-btn ps-btn--primary ps-btn--sm" to={mapFocusHref(pet.pet_id)}>
            <Icon name="pin" size={16} />
            看看 {name} 走到哪
          </Link>
        ) : status === "asleep" || status === "arriving" ? (
          <Link className="ps-btn ps-btn--primary ps-btn--sm" to="/communicator">
            <Icon name="chat" size={16} />
            {status === "asleep" ? `给 ${name} 留句话` : `和 ${name} 说说话`}
          </Link>
        ) : null}
        {withHome ? (
          <Link className="ps-btn ps-btn--secondary ps-btn--sm" to={HOME_TAB.to}>
            回小屋
          </Link>
        ) : null}
      </div>
      {status === "asleep" ? <p className="ps-play__away-hint">睡着也能说，{name} 醒了会回你。</p> : null}
    </section>
  );
}
